import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ChurchBrandMark } from '@/components/church-brand-mark';
import { GivePageShell } from '@/components/give-page-shell';
import { GivingTermsDialog } from '@/components/giving-terms-dialog';
import { PageLoading, PageMessage } from '@/components/page-state';
import { PoweredByFooter } from '@/components/powered-by-footer';
import { SegmentTiles } from '@/components/segment-tiles';
import { api, ApiError } from '@/lib/api';
import { formatMoney } from '@/lib/format';
import { hasAcceptedGivingTerms, rememberGivingTermsAccepted } from '@/lib/giving-terms';
import type { GivingLinkPage, PaymentInitiateResponse } from '@/types/giving';

const gatewayPath = (churchSlug: string, linkSlug: string) =>
  `/gateway/giving/${encodeURIComponent(churchSlug)}/${encodeURIComponent(linkSlug)}`;

export const GivePage = () => {
  const { t, i18n } = useTranslation();
  const { churchSlug = '', linkSlug = '' } = useParams();
  const [link, setLink] = useState<GivingLinkPage | null>(null);
  const [loadState, setLoadState] = useState<
    'loading' | 'ready' | 'notFound' | 'unavailable' | 'error'
  >('loading');
  const [selectedFundId, setSelectedFundId] = useState<string | null>(null);
  const [selectedAmountMinor, setSelectedAmountMinor] = useState<number | null>(null);
  const [customAmountMajor, setCustomAmountMajor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<'states.error' | 'states.paymentsPaused' | null>(
    null,
  );
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    if (!churchSlug || !linkSlug) return;

    const load = async () => {
      setLoadState('loading');
      try {
        const data = await api.get<GivingLinkPage>(gatewayPath(churchSlug, linkSlug));
        setLink(data);
        const defaultFund = data.funds.find((fund) => fund.isDefault) ?? data.funds[0];
        setSelectedFundId(defaultFund?.id ?? null);
        setSelectedAmountMinor(data.suggestedAmountsMinor[0] ?? null);
        setLoadState('ready');
      } catch (error) {
        setLink(null);
        if (error instanceof ApiError) {
          if (error.code === 'GIVING_LINK_NOT_FOUND' || error.status === 404) {
            setLoadState('notFound');
            return;
          }
          if (error.code === 'ENDPOINT_NOT_FOUND') {
            setLoadState('unavailable');
            return;
          }
        }
        setLoadState('error');
      }
    };

    void load();
  }, [churchSlug, linkSlug]);

  const selectedFund = useMemo(
    () => link?.funds.find((fund) => fund.id === selectedFundId) ?? null,
    [link, selectedFundId],
  );

  const selectedCurrency = selectedFund?.currency ?? link?.currency ?? 'GHS';

  const amountMinor = useMemo(() => {
    if (selectedAmountMinor !== null) return selectedAmountMinor;
    const parsed = Number.parseFloat(customAmountMajor);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.round(parsed * 100);
  }, [customAmountMajor, selectedAmountMinor]);

  const startPayment = useCallback(async () => {
    if (!link || !selectedFundId || amountMinor === null) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const returnUrl = window.location.href;
      const response = await api.post<PaymentInitiateResponse>(
        `${gatewayPath(churchSlug, linkSlug)}/payments`,
        {
          fundId: selectedFundId,
          contributionType: link.defaultContributionType,
          amountMinor,
          currency: selectedCurrency,
          attemptId: crypto.randomUUID(),
          returnUrl,
          cancelUrl: returnUrl,
        },
      );

      const redirectUrl =
        response.redirectUrl ??
        response.authorisationUrl ??
        (response.outcome?.kind === 'redirect' ? response.outcome.authorisationUrl : undefined);

      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      setSubmitError('states.error');
    } catch (error) {
      setSubmitError(
        error instanceof ApiError && error.code === 'GIVING_PAYMENTS_SUSPENDED'
          ? 'states.paymentsPaused'
          : 'states.error',
      );
    } finally {
      setSubmitting(false);
    }
  }, [amountMinor, churchSlug, link, linkSlug, selectedCurrency, selectedFundId]);

  const onGive = useCallback(() => {
    if (!link || !selectedFundId || amountMinor === null) return;
    if (!hasAcceptedGivingTerms()) {
      setTermsOpen(true);
      return;
    }
    void startPayment();
  }, [amountMinor, link, selectedFundId, startPayment]);

  const onTermsAccepted = useCallback(() => {
    rememberGivingTermsAccepted();
    setTermsOpen(false);
    void startPayment();
  }, [startPayment]);

  if (loadState === 'loading') {
    return <PageLoading />;
  }

  if (loadState === 'notFound') {
    return <PageMessage message={t('states.notFound')} />;
  }

  if (loadState === 'unavailable' || loadState === 'error' || !link) {
    return (
      <PageMessage
        message={loadState === 'unavailable' ? t('states.unavailable') : t('states.error')}
      />
    );
  }

  const fundOptions = link.funds.map((fund) => ({
    value: fund.id,
    label: fund.name,
  }));

  const amountOptions = link.suggestedAmountsMinor.map((amount) => ({
    value: String(amount),
    label: formatMoney(amount, selectedCurrency, i18n.language),
  }));

  const customAmountId = 'give-custom-amount';

  return (
    <>
      <GivePageShell footer={<PoweredByFooter />}>
        <div className="give-card give-card--form stack">
        <ChurchBrandMark
          churchName={link.church.name}
          campusName={link.church.campusName}
          logoUrl={link.church.logoUrl}
          heroImageUrl={link.link.imageUrl}
          title={link.link.title}
          description={link.link.description}
        />

        <section className="stack-tight">
          <p className="field-label">{t('page.fund')}</p>
          <SegmentTiles
            ariaLabel={t('page.fund')}
            options={fundOptions}
            value={selectedFundId}
            onChange={setSelectedFundId}
            disabled={submitting}
          />
        </section>

        <section className="stack-tight">
          <p className="field-label">{t('page.chooseAmount')}</p>
          <SegmentTiles
            ariaLabel={t('page.chooseAmount')}
            options={amountOptions}
            value={selectedAmountMinor !== null ? String(selectedAmountMinor) : null}
            onChange={(value) => {
              setSelectedAmountMinor(Number(value));
              setCustomAmountMajor('');
            }}
            disabled={submitting}
            amountStyle
          />
          {link.allowCustomAmount ? (
            <div className="give-field">
              <label className="field-label" htmlFor={customAmountId}>
                {t('page.customAmount')}
              </label>
              <input
                id={customAmountId}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="give-control give-control--amount"
                value={customAmountMajor}
                disabled={submitting}
                placeholder={formatMoney(10000, selectedCurrency, i18n.language)}
                onChange={(event) => {
                  setCustomAmountMajor(event.target.value);
                  setSelectedAmountMinor(null);
                }}
              />
            </div>
          ) : null}
        </section>

        <div className="give-actions give-actions--wide">
          <button
            type="button"
            className="primary"
            disabled={submitting || !selectedFundId || amountMinor === null}
            onClick={onGive}
          >
            {submitting ? t('page.giving') : t('page.give')}
          </button>
        </div>

        {submitError ? (
          <p className="give-message give-message--error" role="alert">
            {t(submitError)}
          </p>
        ) : null}
        </div>
      </GivePageShell>
      <GivingTermsDialog
        open={termsOpen}
        onAccepted={onTermsAccepted}
        onCancel={() => setTermsOpen(false)}
      />
    </>
  );
};
